import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { mergeDuplicatesAndSync, updateItemAndSync } from '../../redux/inventorySlice';
import {
  learnAlias,
  learnDistinct,
  learnZone,
  savePantryLearning,
} from '../../redux/pantryLearningSlice';
import { primaryKey } from '../../utils/pantryDuplicates';
import { trackEvent } from '../../services/analyticsService';
import { RootState, AppDispatch } from '../../redux/store';
import type { TidyDecisions } from './DedupeContent';
import { buildMoveUpdates } from './usePantryActions';

/** Every unordered pair within a group, so a 3-row group teaches three lessons. */
function pairsOf<T>(members: T[]): Array<[T, T]> {
  const pairs: Array<[T, T]> = [];
  for (let i = 0; i < members.length; i++) {
    for (let j = i + 1; j < members.length; j++) pairs.push([members[i], members[j]]);
  }
  return pairs;
}

/**
 * Apply what the user decided in the tidy inbox — and remember it.
 *
 * The remembering is the point. Merging rows tidies the pantry once; recording
 * that these two names mean the same food (and those two do not) is what stops
 * the same question being asked again next week. Both layouts share this hook
 * so a lesson cannot be captured on one and dropped on the other.
 */
export function useTidyActions(flash: (message: string) => void) {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const userId = useSelector((state: RootState) => state.auth?.user?.uid);
  const householdId = useSelector((state: RootState) => state.auth?.householdId);

  return function applyDecisions({ merge, separate, placements }: TidyDecisions) {
    const itemsRemoved = merge.reduce((sum, group) => sum + group.length - 1, 0);

    // Learn before merging: the merge deletes the absorbed rows, and their
    // names are the evidence.
    //
    // Keyed on the normalized key rather than the display text, so the lesson
    // survives the surviving row later being renamed.
    for (const group of merge) {
      const keys = group.map(item => primaryKey(item.name));
      for (const [a, b] of pairsOf(keys)) dispatch(learnAlias({ a, b }));
    }

    if (merge.length > 0) {
      dispatch(
        mergeDuplicatesAndSync({
          groups: merge.map(group => group.map(item => item.id)),
          userId,
        })
      );
    }

    // A rejected merge is the more valuable signal: it is the only way the
    // matcher learns that two names its rules collapsed are different foods.
    for (const group of separate) {
      const keys = group.map(item => primaryKey(item.name));
      for (const [a, b] of pairsOf(keys)) dispatch(learnDistinct({ a, b }));
    }

    // Same rule as the Move sheet, deliberately: placing a row from the tidy
    // inbox is a move, and a move re-dates the food for where it now lives.
    // This wrote `location` on its own for a while, which made the tray the one
    // path that could shelve something in the freezer without giving it the
    // freezer's shelf life.
    for (const { item, zone } of placements) {
      dispatch(
        updateItemAndSync({
          id: item.id,
          updates: { ...buildMoveUpdates(item, zone), needsSorting: false },
          userId,
        })
      );
      dispatch(learnZone({ key: primaryKey(item.name), zone }));
    }

    if (merge.length || separate.length || placements.length) {
      dispatch(savePantryLearning({ userId, householdId }));
    }

    trackEvent('pantry_tidy_applied', {
      merged: merge.length,
      itemsRemoved,
      separated: separate.length,
      placed: placements.length,
    });

    const done = itemsRemoved + placements.length;
    flash(t(`pantry.tidy.done_${done === 1 ? 'one' : 'other'}`, { count: done }));
  };
}
